import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { TemplateModel, UpdateWebsiteRequest, WebsiteResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { FiltersButton, Button, Input, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { WebsiteCard } from '../components/WebsiteCard';
import { WebsiteListItem } from '../components/WebsiteListItem';
import { ModelSelector } from '../components/ModelSelector';
import {
  useWebsites,
  useCreateWebsite,
  useDeleteWebsite,
  useUpdateWebsite,
  usePublishWebsite,
  useUnpublishWebsite,
} from '../hooks/useWebsites';
import { buildExportPayload, slugifyFilename, downloadJson } from '../utils/websiteTransfer';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';

type SortKey = 'name' | 'status' | 'model' | 'updated';
type StatusValue = 'draft' | 'published' | 'archived';
type ViewMode = 'grid' | 'table';

const PAGE_SIZE = 12;
const STATUS_RANK: Record<string, number> = { published: 0, draft: 1, archived: 2 };
const STATUS_LABEL: Record<StatusValue, string> = {
  published: 'Publicado',
  draft: 'Rascunho',
  archived: 'Arquivado',
};

/** Override de CSS variables para tema claro fixo (modal). */
const MODAL_LIGHT_VARS: React.CSSProperties = {
  '--theme-text': '#1e293b',
  '--theme-text-primary': '#1e293b',
  '--theme-text-secondary': '#334155',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-error-color': '#ef4444',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-primary': '#8b5cf6',
  '--theme-button-primary': '#8b5cf6',
  '--theme-button-primary-text': '#ffffff',
  '--theme-button-primary-hover': '#7c3aed',
  '--theme-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-component-background': '#ffffff',
} as React.CSSProperties;

export const WebsitesListPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();

  // Listagem / filtros / ordenação (client-side sobre a página carregada)
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusValue | undefined>(undefined);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'updated', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Criação
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedModel, setSelectedModel] = useState<TemplateModel | null>(null);

  const { data, isLoading, error } = useWebsites({ page: 1, limit: 50 });
  const createMutation = useCreateWebsite();
  const deleteMutation = useDeleteWebsite();
  const updateMutation = useUpdateWebsite();
  const publishMutation = usePublishWebsite();
  const unpublishMutation = useUnpublishWebsite();

  const items = useMemo(() => data?.items ?? [], [data]);

  // Busca (nome ou domínio) + filtro de status — client-side.
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return items.filter((w) => {
      if (statusFilter && w.status !== statusFilter) return false;
      if (!q) return true;
      const inName = w.name.toLowerCase().includes(q);
      const inDomain = (w.domains ?? []).some((d) => d.toLowerCase().includes(q));
      return inName || inDomain;
    });
  }, [items, searchTerm, statusFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'model') cmp = a.modelId.localeCompare(b.modelId, 'pt-BR');
      else if (sort.key === 'status') cmp = (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9);
      else {
        const ta = new Date(a.updatedAt ?? a.createdAt).getTime();
        const tb = new Date(b.updatedAt ?? b.createdAt).getTime();
        cmp = ta - tb;
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sorted, currentPage],
  );

  const selectedCount = selectedIds.size;
  const allOnPageSelected = pageItems.length > 0 && pageItems.every((w) => selectedIds.has(w.id));
  const someOnPageSelected = pageItems.some((w) => selectedIds.has(w.id));

  const activeFilterCount = statusFilter ? 1 : 0;
  const statusLabel = statusFilter ? STATUS_LABEL[statusFilter] : null;

  // ── Setters de filtro (resetam paginação) ──
  const applySearch = (term: string) => { setSearchTerm(term); setPage(1); };
  const applyStatus = (status?: StatusValue) => { setStatusFilter(status); setPage(1); setFiltersOpen(false); };
  const clearAllFilters = () => { setStatusFilter(undefined); setPage(1); setFiltersOpen(false); };

  const handleSort = (key: SortKey) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    setPage(1);
  };

  // ── Seleção ──
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageItems.forEach((w) => next.delete(w.id));
      else pageItems.forEach((w) => next.add(w.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  // ── Ações por item ──
  const handleEdit = (id: string) => navigate(`/websites/${id}`);

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Tem certeza que deseja excluir este website?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteMutation.mutate(id);
  };

  const handleImport = async (id: string, payload: UpdateWebsiteRequest) => {
    try {
      await updateMutation.mutateAsync({ id, data: payload });
      toast.success('Configuração importada com sucesso.');
    } catch {
      // Erro já exibido pelo interceptor global do axios
    }
  };

  const handlePublish = (id: string) => publishMutation.mutate(id);
  const handleUnpublish = (id: string) => unpublishMutation.mutate(id);

  // ── Ações em lote ──
  const selectedWebsites = (): WebsiteResponse[] => items.filter((w) => selectedIds.has(w.id));

  const bulkPublish = () => {
    selectedWebsites().filter((w) => w.status !== 'published').forEach((w) => publishMutation.mutate(w.id));
    clearSelection();
  };

  const bulkUnpublish = () => {
    selectedWebsites().filter((w) => w.status === 'published').forEach((w) => unpublishMutation.mutate(w.id));
    clearSelection();
  };

  const bulkExport = () => {
    selectedWebsites().forEach((w) => downloadJson(slugifyFilename(w.name), buildExportPayload(w)));
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} website(s) selecionado(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    selectedWebsites().forEach((w) => deleteMutation.mutate(w.id));
    clearSelection();
  };

  // ── Criação ──
  const handleCreate = async () => {
    if (!selectedModel || !newName.trim()) return;
    try {
      const result = await createMutation.mutateAsync({ name: newName.trim(), modelId: selectedModel.id });
      closeCreateModal();
      navigate(`/websites/${result.id}`);
    } catch {
      // Error handled by React Query
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewName('');
    setSelectedModel(null);
  };

  // ── Helpers de render ──
  const renderCheck = (on: boolean, mixed = false, onClick?: () => void, label?: string) => (
    <button
      type="button"
      onClick={onClick}
      className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors flex-shrink-0"
      style={{
        backgroundColor: on || mixed ? 'var(--theme-primary)' : 'transparent',
        borderColor: on || mixed ? 'var(--theme-primary)' : 'var(--theme-input-border)',
        color: '#ffffff',
      }}
      aria-label={label}
      data-testid="website-select-all"
    >
      {on && <Icon name="check" className="w-3 h-3" />}
      {!on && mixed && <span className="w-2 h-0.5 rounded-full bg-white" />}
    </button>
  );

  const SortHead: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <th
      className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap cursor-pointer select-none"
      style={{ color: 'var(--theme-muted-text)' }}
      onClick={() => handleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} className="w-3 h-3" />}
      </span>
    </th>
  );

  // F34: cards SEM checkbox — seleção em massa só na view de lista/tabela
  const renderCard = (w: WebsiteResponse) => (
    <WebsiteCard
      key={w.id}
      website={w}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onImport={handleImport}
      onPublish={handlePublish}
      onUnpublish={handleUnpublish}
    />
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas ── */}
      <PageToolbar
        left={<>
          {/* Esquerda: breadcrumb + contador */}
          <span className="font-bold text-xl tracking-tight truncate" style={{ color: 'var(--theme-text)' }}>Websites</span>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total} sites
            </span>
          )}
        </>}
        center={
          /* Centro: busca (sempre centralizada — F17-22) */
          <ToolbarSearch
            value={searchTerm}
            onChange={applySearch}
            placeholder="Buscar por nome ou domínio…"
            data-testid="websites-search"
          />
        }
        right={<>
          {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="websites-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                >
                  <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
                  {([[undefined, 'Todos'], ['published', 'Publicado'], ['draft', 'Rascunho'], ['archived', 'Arquivado']] as const).map(([val, label]) => {
                    const active = statusFilter === val;
                    return (
                      <button
                        key={label}
                        onClick={() => applyStatus(val)}
                        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
                        style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`websites-filter-status-${val ?? 'all'}`}
                      >
                        {label}
                        {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="websites-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Novo site (único primário — F01-03: Button shared 38px, colapso @container) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => setShowCreateModal(true)}
            data-testid="new-website"
          >
            Novo site
          </Button>
        </>}
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => applyStatus(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }}>Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          {/* Bulk-bar compacta: Button shared `sm` (30px) — padrão secundário com ícone 16 + hover (F08) */}
          <Button variant="secondary" size="sm" icon="check-circle" onClick={bulkPublish} data-testid="websites-bulk-publish">
            Publicar
          </Button>
          <Button variant="secondary" size="sm" icon="eye-off" onClick={bulkUnpublish} data-testid="websites-bulk-unpublish">
            Despublicar
          </Button>
          <Button variant="secondary" size="sm" icon="download" onClick={bulkExport} data-testid="websites-bulk-export">
            Exportar
          </Button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="websites-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="websites-bulk-clear">Limpar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar websites: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando websites…" />}

          {!isLoading && !error && sorted.length === 0 && (
            <ListPageEmpty
              icon="globe"
              message="Nenhum website encontrado"
              description={searchTerm || activeFilterCount > 0 ? 'Ajuste a busca ou os filtros.' : 'Crie seu primeiro website escolhendo um dos modelos disponíveis.'}
              actionLabel="Novo site"
              onAction={() => setShowCreateModal(true)}
            />
          )}

          {/* Grid (Cards) */}
          {!isLoading && !error && sorted.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {pageItems.map(renderCard)}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && sorted.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {pageItems.map(renderCard)}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <th className="pl-4 pr-1 py-3 w-11">
                        {renderCheck(allOnPageSelected, someOnPageSelected && !allOnPageSelected, toggleSelectAllOnPage, 'Selecionar todos')}
                      </th>
                      <SortHead label="Nome" k="name" />
                      <SortHead label="Status" k="status" />
                      <SortHead label="Modelo" k="model" />
                      <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Domínios</th>
                      <SortHead label="Atualizado" k="updated" />
                      <th className="px-4 py-3 w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((w) => (
                      <WebsiteListItem
                        key={w.id}
                        website={w}
                        selected={selectedIds.has(w.id)}
                        onToggleSelect={toggleSelect}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onImport={handleImport}
                        onPublish={handlePublish}
                        onUnpublish={handleUnpublish}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação (client-side) */}
          {!isLoading && !error && totalPages > 1 && (
            <ListPagePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => setPage(p)}
            />
          )}
        </div>
      </div>

      {/* ── Modal: novo website (tema claro fixo) ── */}
      {showCreateModal && createPortal((
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}
          /* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
        >
          <div
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
            style={{ backgroundColor: '#ffffff', ...MODAL_LIGHT_VARS }}
            onClick={(e) => e.stopPropagation()}
            data-testid="website-create-modal"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid #eef1f5' }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-none" style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}>
                <Icon name="globe" className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-semibold flex-1" style={{ color: '#1e293b' }}>Novo website</h2>
              <button
                type="button"
                onClick={closeCreateModal}
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ color: '#64748b' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                aria-label="Fechar"
                data-testid="website-create-close"
              >
                <Icon name="x" className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#334155' }}>
                  Nome do website <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <Input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Ex: Site da Imobiliária"
                  data-testid="website-create-name"
                />
              </div>

              <label className="block text-sm font-semibold mb-3" style={{ color: '#334155' }}>
                Escolha um modelo <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <ModelSelector
                onSelect={setSelectedModel}
                selectedModelId={selectedModel?.id}
                isModal
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-5 py-4" style={{ borderTop: '1px solid #eef1f5' }}>
              <button
                type="button"
                onClick={closeCreateModal}
                className="h-9 px-4 rounded-lg text-sm font-semibold border"
                style={{ borderColor: '#e2e6ec', color: '#475569', backgroundColor: '#ffffff' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                data-testid="website-create-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={!selectedModel || !newName.trim() || createMutation.isPending}
                className="h-9 px-4 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#8b5cf6' }}
                onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#7c3aed'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
                data-testid="website-create-submit"
              >
                {createMutation.isPending ? 'Criando…' : 'Criar website'}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
};
